import Dexie, { type EntityTable } from 'dexie';
// Type-only, so this stays a compile-time reference and creates no import cycle with the
// API client. The cache stores exactly what the endpoint returns; a second near-identical
// interface would only be a second thing to forget to update.
import type {
  ConversationSummary,
  DirectMessagePayload,
  TicketMessagePayload,
  TicketSummary,
} from '@nbh/shared';
import type { LocationOption, SessionUser, TicketDetailResponse } from './api';
import type { AdminAsset } from './adminApi';

/**
 * Local-first storage. CLAUDE.md Rule 4: every write lands in IndexedDB first and
 * syncs afterwards, so the app never depends on the network being there.
 *
 * The outbox is the generic write queue. `locations` is the first read cache: the fault
 * picker is the entry point to the whole system and it was the one screen that could not
 * render without the network, which Rule 4 does not allow. Further domain tables
 * (tickets, assets, inventory) follow the same shape.
 */

export type OutboxStatus = 'pending' | 'syncing' | 'failed';

export interface OutboxEntry {
  id: number;
  /** HTTP method to replay against the API. */
  method: 'POST' | 'PATCH' | 'DELETE';
  /** API path relative to /api, e.g. "/tickets". */
  path: string;
  body?: unknown;
  /** UTC ISO timestamp of when the user performed the action, not when it synced. */
  createdAt: string;
  attempts: number;
  status: OutboxStatus;
  lastError?: string;
}

/** Small key/value store for sync bookkeeping. */
export interface MetaEntry {
  key: string;
  value: string;
}

/**
 * One cached queue, keyed by the filter that produced it.
 *
 * The list is filtered *and sorted* server-side, so the rows are stored as the server
 * ordered them rather than as loose tickets to be re-filtered here. Reproducing that
 * ordering on the client would mean guessing, and TicketQueue already refuses to guess a
 * ticket's position for exactly this reason. What this offers offline is honest: the last
 * thing this device saw for this filter.
 */
export interface CachedTicketList {
  key: string;
  tickets: TicketSummary[];
  cachedAt: string;
}

/** Stable key for a filter object — same filter, same key, whatever the field order. */
export function ticketListKey(filter: Record<string, unknown>): string {
  const entries = Object.entries(filter)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([a], [b]) => a.localeCompare(b));
  return entries.length === 0 ? 'all' : entries.map(([k, v]) => `${k}=${String(v)}`).join('&');
}

/**
 * The chat caches (D9 of the chat brief). A thread is stored whole, in server order, under
 * the id it was fetched by — the same shape as a queue row, for the same reason: what this
 * offers offline is the last thing this device saw for this thread, not a reconstruction.
 */
export interface CachedTicketThread {
  ticketId: string;
  messages: TicketMessagePayload[];
  cachedAt: string;
}

export interface CachedDirectThread {
  conversationId: string;
  messages: DirectMessagePayload[];
  cachedAt: string;
}

/** The inbox, one row: a thread cannot be opened offline from a list that is not there. */
export interface CachedInbox {
  key: 'conversations';
  conversations: ConversationSummary[];
  cachedAt: string;
}

/** Last moment this device successfully reached the API. */
const LAST_SYNC_KEY = 'lastSyncAt';
const SESSION_KEY = 'sessionUser';

class NbhDatabase extends Dexie {
  declare outbox: EntityTable<OutboxEntry, 'id'>;
  declare meta: EntityTable<MetaEntry, 'key'>;
  declare locations: EntityTable<LocationOption, 'id'>;
  declare ticketLists: EntityTable<CachedTicketList, 'key'>;
  declare ticketDetails: EntityTable<TicketDetailResponse, 'id'>;
  declare assets: EntityTable<AdminAsset, 'id'>;
  declare ticketThreads: EntityTable<CachedTicketThread, 'ticketId'>;
  declare directThreads: EntityTable<CachedDirectThread, 'conversationId'>;
  declare inbox: EntityTable<CachedInbox, 'key'>;

  constructor() {
    super('nbh-ops');
    this.version(1).stores({
      outbox: '++id, status, createdAt',
    });
    this.version(2).stores({
      outbox: '++id, status, createdAt',
      meta: 'key',
    });
    // qrCode is indexed because a scanned sticker resolves through it, and that lookup
    // has to work standing in a basement with no signal. `name` is indexed so the cached
    // list can come back name-ascending like the endpoint's — IndexedDB compares strings
    // binary rather than by Postgres's collation, so mixed-case names can land in a
    // slightly different order offline. That is cosmetic; the set is the same.
    this.version(3).stores({
      outbox: '++id, status, createdAt',
      meta: 'key',
      locations: 'id, qrCode, name',
    });
    // The read caches behind the three screens a technician actually opens. Only the
    // primary keys are indexed: nothing queries these by anything else, and every index
    // is write cost on a phone that is already the slowest part of the system.
    this.version(4).stores({
      outbox: '++id, status, createdAt',
      meta: 'key',
      locations: 'id, qrCode, name',
      ticketLists: 'key',
      ticketDetails: 'id',
      assets: 'id',
    });
    // The chat threads and the inbox (D9 of the chat brief). Primary keys only, as above:
    // a thread is looked up by the id it was fetched under and by nothing else.
    this.version(5).stores({
      outbox: '++id, status, createdAt',
      meta: 'key',
      locations: 'id, qrCode, name',
      ticketLists: 'key',
      ticketDetails: 'id',
      assets: 'id',
      ticketThreads: 'ticketId',
      directThreads: 'conversationId',
      inbox: 'key',
    });
  }
}

export const db = new NbhDatabase();

/** Queue a write for later replay. Returns the local id immediately. */
export async function enqueueWrite(
  entry: Omit<OutboxEntry, 'id' | 'createdAt' | 'attempts' | 'status'>,
): Promise<number> {
  return db.outbox.add({
    ...entry,
    createdAt: new Date().toISOString(),
    attempts: 0,
    status: 'pending',
  } as OutboxEntry);
}

export function countPending(): Promise<number> {
  return db.outbox.where('status').anyOf('pending', 'syncing').count();
}

export function countFailed(): Promise<number> {
  return db.outbox.where('status').equals('failed').count();
}

/**
 * Everything still owed to the server, oldest first.
 *
 * Deliberately *not* filtered by the last sync checkpoint. An entry queued before the
 * previous sync and still pending is one that failed to send — excluding it would
 * silently discard the report the outbox exists to protect. Age is a reason to send
 * something sooner, never a reason to drop it.
 */
export function getPendingWrites(): Promise<OutboxEntry[]> {
  return db.outbox.where('status').anyOf('pending', 'failed').sortBy('createdAt');
}

/** Reads the sync checkpoint. Null before this device has ever synced. */
export async function getLastSyncAt(): Promise<Date | null> {
  const row = await db.meta.get(LAST_SYNC_KEY);
  return row ? new Date(row.value) : null;
}

/**
 * Advances the sync checkpoint. Only ever called *after* pending writes have been
 * dispatched — see services/outboxSync.
 */
export async function setLastSyncAt(at: Date): Promise<void> {
  await db.meta.put({ key: LAST_SYNC_KEY, value: at.toISOString() });
}

/**
 * Remembers who is signed in, so a cold start with no signal is not a sign-out.
 *
 * Safe to persist because it is not a credential: the token lives in an httpOnly cookie
 * this code cannot read, and the server re-authorises every request regardless. Holding
 * the profile grants nothing — it only spares the app from having to ask the network for
 * a name it already knows before it will render a queue it already has.
 */
export async function cacheSessionUser(user: SessionUser): Promise<void> {
  await db.meta.put({ key: SESSION_KEY, value: JSON.stringify(user) });
}

export async function getCachedSessionUser(): Promise<SessionUser | null> {
  const row = await db.meta.get(SESSION_KEY);
  if (!row) return null;
  try {
    return JSON.parse(row.value) as SessionUser;
  } catch {
    // Written by an older build with a different shape. Treat as absent.
    return null;
  }
}

/** Signing out, or being told the session is gone, must leave nothing behind. */
export async function clearCachedSessionUser(): Promise<void> {
  await db.meta.delete(SESSION_KEY);
}

/**
 * Empties every read cache that belongs to whoever was signed in.
 *
 * These four are keyed by filter or by row id and never by user, so without this the
 * next person to sign in on a shared handset renders the previous person's queue and
 * job details before the network replaces them. Between two members of staff that
 * discloses nothing new — `canSeeTicket` lets every non-vendor role see every job — but
 * for a vendor it walks straight past the one boundary the system has, showing jobs
 * every live route would 404 them from.
 *
 * Two things are deliberately left alone.
 *
 * The **outbox** survives: it holds work this device has done and not yet sent, and a
 * sign-out is not a reason to destroy a fault report nobody has received. It replays on
 * the next connection under whoever's session is current then, which is the same
 * behaviour as a sign-out mid-queue today.
 *
 * The **`lastSyncAt` checkpoint** in `meta` survives, and only `SESSION_KEY` is deleted
 * from that table rather than clearing it. That timestamp is this device's proof of when
 * it was last online, and the server clamps backdated reports against it (D18). Wiping
 * it would leave the handset looking brand new, with no floor, and the next backdated
 * report taken at face value — the same defect as an outbox replay that sends no device
 * id, arrived at from the other direction.
 */
export async function clearUserScopedCaches(): Promise<void> {
  await Promise.all([
    db.ticketLists.clear(),
    db.ticketDetails.clear(),
    db.locations.clear(),
    db.assets.clear(),
    // Conversations are the most personal thing on the device; they leave with the session.
    db.ticketThreads.clear(),
    db.directThreads.clear(),
    db.inbox.clear(),
  ]);
}

export async function cacheTicketThread(
  ticketId: string,
  messages: TicketMessagePayload[],
): Promise<void> {
  await db.ticketThreads.put({ ticketId, messages, cachedAt: new Date().toISOString() });
}

/** Null when this device never opened the thread — the same distinction `getCachedTicketList` draws. */
export async function getCachedTicketThread(
  ticketId: string,
): Promise<TicketMessagePayload[] | null> {
  const row = await db.ticketThreads.get(ticketId);
  return row ? row.messages : null;
}

export async function cacheDirectThread(
  conversationId: string,
  messages: DirectMessagePayload[],
): Promise<void> {
  await db.directThreads.put({ conversationId, messages, cachedAt: new Date().toISOString() });
}

export async function getCachedDirectThread(
  conversationId: string,
): Promise<DirectMessagePayload[] | null> {
  const row = await db.directThreads.get(conversationId);
  return row ? row.messages : null;
}

export async function cacheInbox(conversations: ConversationSummary[]): Promise<void> {
  await db.inbox.put({ key: 'conversations', conversations, cachedAt: new Date().toISOString() });
}

export async function getCachedInbox(): Promise<ConversationSummary[] | null> {
  const row = await db.inbox.get('conversations');
  return row ? row.conversations : null;
}

export async function removeOutboxEntry(id: number): Promise<void> {
  await db.outbox.delete(id);
}

/**
 * Stores places for the next time there is no signal.
 *
 * `authoritative` is the whole subtlety. An unfiltered `GET /locations` is the complete
 * set of active places, so it replaces the table — that is what lets a retired place
 * eventually leave the cache. A `?qrCode=` response is one row and says nothing about the
 * rest, so it upserts. Replacing on that would shrink the cache to a single room and the
 * next offline visit would show a property with one place in it.
 */
export async function cacheLocations(
  places: LocationOption[],
  authoritative: boolean,
): Promise<void> {
  await db.transaction('rw', db.locations, async () => {
    if (authoritative) await db.locations.clear();
    await db.locations.bulkPut(places);
  });
}

/**
 * The places this device last saw, standing in for the endpoint when it cannot be reached.
 *
 * Filtering by qrCode here mirrors what the server does with the same parameter, so a
 * scanned sticker resolves to its room offline exactly as it does online.
 *
 * These rows can be stale: a place retired since the last successful fetch is still
 * offered. That is the accepted cost of working with no signal — a report filed against
 * it reaches a server that can still name the place, which beats not being able to report
 * the fault at all.
 */
export async function getCachedLocations(qrCode?: string): Promise<LocationOption[]> {
  if (qrCode) return db.locations.where('qrCode').equals(qrCode).toArray();
  return db.locations.orderBy('name').toArray();
}

export async function cacheTicketList(
  filter: Record<string, unknown>,
  tickets: TicketSummary[],
): Promise<void> {
  await db.ticketLists.put({
    key: ticketListKey(filter),
    tickets,
    cachedAt: new Date().toISOString(),
  });
}

/**
 * The queue this device last saw for this filter, or null if it never saw one.
 *
 * Null and an empty array mean different things and the difference is the whole point: no
 * cached row is "we do not know", which must fall through to an error when the network is
 * also gone; a cached empty array is "there was genuinely no work", which is an answer and
 * should render as the empty state.
 */
export async function getCachedTicketList(
  filter: Record<string, unknown>,
): Promise<TicketSummary[] | null> {
  const row = await db.ticketLists.get(ticketListKey(filter));
  return row ? row.tickets : null;
}

export async function cacheTicketDetail(ticket: TicketDetailResponse): Promise<void> {
  await db.ticketDetails.put(ticket);
}

export async function getCachedTicketDetail(id: string): Promise<TicketDetailResponse | null> {
  return (await db.ticketDetails.get(id)) ?? null;
}

/**
 * Assets are replaced wholesale like locations, and for the same reason: an unfiltered
 * list is the complete set, and replacing is what lets a retired asset leave the cache.
 * A search-filtered response is not cached at all — it is a subset by construction, and
 * an admin typing a query is at a desk, not in a plant room.
 */
export async function cacheAssets(assets: AdminAsset[]): Promise<void> {
  await db.transaction('rw', db.assets, async () => {
    await db.assets.clear();
    await db.assets.bulkPut(assets);
  });
}

export async function getCachedAssets(): Promise<AdminAsset[] | null> {
  const rows = await db.assets.toArray();
  return rows.length > 0 ? rows : null;
}

/**
 * Drops the cached register on its own, without touching the other caches.
 *
 * Separate from `clearUserScopedCaches` because the trigger is different. That one runs
 * at sign-out, when everything user-scoped should go. This one runs when a session's
 * role stops permitting the equipment register while the session itself continues — the
 * queue and the locations underneath it are still legitimately theirs, and clearing them
 * would take an offline-capable screen away from someone mid-shift for no reason.
 */
export async function clearCachedAssets(): Promise<void> {
  await db.assets.clear();
}

export async function markOutboxFailed(id: number, error: string): Promise<void> {
  const entry = await db.outbox.get(id);
  if (!entry) return;

  await db.outbox.update(id, {
    status: 'failed',
    attempts: entry.attempts + 1,
    lastError: error,
  });
}
